import moment from "moment-timezone"

const DATE_ONLY_KEY = /^\d{4}-\d{2}-\d{2}$/

function parseUtcMoment(utcString) {
  if (!utcString) return null
  const s = String(utcString).trim()
  if (!s) return null
  const hasExplicitZone = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)
  const normalized = hasExplicitZone ? s : `${s}Z`
  const parsed = moment.utc(normalized)
  return parsed.isValid() ? parsed : null
}

/**
 * Normalize API `postDatetime` to UTC ISO.
 * Naive values such as "2026-08-21T10:00:00" (no Z) are treated as UTC, not browser local.
 */
export function normalizePostDatetimeToUtcISO(utcString) {
  const parsed = parseUtcMoment(utcString)
  return parsed ? parsed.toISOString() : null
}

function ianaIfValid(value) {
  if (typeof value !== "string") return null
  const name = value.trim()
  if (!name) return null
  // Offsets like "-06:00" are not IANA names; DST would not apply.
  if (/^[+-]?\d{1,2}:\d{2}$/.test(name)) return null
  return moment.tz.zone(name) ? name : null
}

function ianaFromTimeZoneValue(tz) {
  if (tz == null || tz === "") return null
  if (typeof tz === "string") return ianaIfValid(tz)
  if (typeof tz !== "object") return null
  return (
    ianaIfValid(tz.name) ||
    ianaIfValid(tz.iana) ||
    ianaIfValid(tz.ianaName) ||
    ianaIfValid(tz.timezone) ||
    ianaIfValid(tz.timeZone) ||
    ianaIfValid(tz.zone)
  )
}

/**
 * IANA name from a client object or timezone string.
 * Prefers `client.timeZone.name` (e.g. "America/Edmonton"). Does not use utcOffset.
 * Does not treat `client.name` (property name) as a timezone.
 * Returns null when missing or invalid so callers can fall back.
 */
export function getClientIanaTimeZone(clientOrTimeZone) {
  if (clientOrTimeZone == null || clientOrTimeZone === "") return null

  if (typeof clientOrTimeZone === "string") return ianaIfValid(clientOrTimeZone)
  if (typeof clientOrTimeZone !== "object") return null

  return (
    ianaFromTimeZoneValue(clientOrTimeZone.timeZone) ||
    ianaFromTimeZoneValue(clientOrTimeZone.timezone) ||
    ianaFromTimeZoneValue(clientOrTimeZone.time_zone) ||
    ianaFromTimeZoneValue(clientOrTimeZone.TimeZone) ||
    ianaIfValid(clientOrTimeZone.timeZoneName) ||
    ianaIfValid(clientOrTimeZone.ianaTimeZone) ||
    ianaIfValid(clientOrTimeZone.iana)
  )
}

/**
 * Prefer the selected client; if it has no IANA zone, use the matching row from the clients list.
 */
export function resolveSchedulingClient(selectedClient, clients = []) {
  if (getClientIanaTimeZone(selectedClient)) return selectedClient
  const id = selectedClient?.id ?? selectedClient?.clientId
  if (id == null) return selectedClient
  const sid = String(id)
  const match = (Array.isArray(clients) ? clients : []).find(
    (c) => String(c?.id ?? "") === sid || String(c?.clientId ?? "") === sid,
  )
  return getClientIanaTimeZone(match) ? match : selectedClient
}

/** Calendar cell / picker date key. Date-only strings are used as-is (not treated as UTC). */
export function toPickerDateKey(value, fallbackDateKey = "") {
  if (value == null || value === "") return fallbackDateKey
  if (typeof value === "string") {
    const key = value.trim().slice(0, 10)
    return DATE_ONLY_KEY.test(key) ? key : fallbackDateKey
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear()
    const m = String(value.getMonth() + 1).padStart(2, "0")
    const d = String(value.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
  return fallbackDateKey
}

/**
 * Zone used for Create Post / calendar scheduling.
 * Falls back to the browser timezone (then UTC) when the client has no IANA zone.
 */
export function resolveSchedulingTimeZone(clientOrTimeZone) {
  return getClientIanaTimeZone(clientOrTimeZone) || moment.tz.guess() || "UTC"
}

export function nowInTimeZone(clientOrTimeZone) {
  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  const local = moment.tz(tz)
  return {
    date: local.format("YYYY-MM-DD"),
    time: local.format("HH:mm"),
    timeZone: tz,
  }
}

function formatUtcOffsetLabel(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? "+" : "-"
  const abs = Math.abs(Number(offsetMinutes) || 0)
  const hours = Math.floor(abs / 60)
  const mins = abs % 60
  if (mins === 0) return `UTC${sign}${hours}:00`
  if (mins === 30) return `UTC${sign}${hours}.5:00`
  return `UTC${sign}${hours}:${String(mins).padStart(2, "0")}`
}

/**
 * CTZ / UTC / Local times for the Create Post hover tooltip.
 * `dateStr` + `timeStr` are wall-clock values in the client timezone.
 */
export function buildSchedulingTimeComparison(clientOrTimeZone, dateStr, timeStr) {
  if (!dateStr || !timeStr) return null

  const clientTz = resolveSchedulingTimeZone(clientOrTimeZone)
  const browserTz = moment.tz.guess() || "UTC"
  const inClient = moment.tz(
    `${String(dateStr).trim()} ${String(timeStr).trim()}`,
    "YYYY-MM-DD HH:mm",
    true,
    clientTz,
  )
  if (!inClient.isValid()) return null

  const utc = inClient.clone().utc()
  const inBrowser = inClient.clone().tz(browserTz)
  const formatTime = (m) => m.format("hh:mm A")

  return {
    ctz: {
      label: `CTZ (${formatUtcOffsetLabel(inClient.utcOffset())})`,
      time: formatTime(inClient),
    },
    utc: {
      label: "UTC",
      time: formatTime(utc),
    },
    local: {
      label: `Local (${formatUtcOffsetLabel(inBrowser.utcOffset())})`,
      time: formatTime(inBrowser),
    },
  }
}

/**
 * Convert picker date + time → UTC ISO for the API.
 * `dateStr`/`timeStr` are wall-clock values in `clientOrTimeZone` (client IANA, else browser).
 */
export const localToUtcISO = (dateStr, timeStr, clientOrTimeZone) => {
  if (!dateStr || !timeStr) return null

  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  const local = moment.tz(
    `${String(dateStr).trim()} ${String(timeStr).trim()}`,
    "YYYY-MM-DD HH:mm",
    true,
    tz,
  )
  if (!local.isValid()) return null
  return local.utc().toISOString()
}

/**
 * Convert UTC ISO → date + time fields for `<input type="date|time">` in the client zone.
 */
export const utcToLocalInput = (utcString, clientOrTimeZone) => {
  if (!utcString) return { date: "", time: "" }

  const parsed = parseUtcMoment(utcString)
  if (!parsed) return { date: "", time: "" }

  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  const local = parsed.clone().tz(tz)

  return {
    date: local.format("YYYY-MM-DD"),
    time: local.format("HH:mm"),
  }
}

function formatDateForZoneDisplay(date, timeZone) {
  if (!date || Number.isNaN(date.getTime())) return ""

  const formatter = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  })
  const parts = formatter.formatToParts(date)
  let out = ""
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]
    if (p.type === "literal") {
      const prev = parts[i - 1]
      const next = parts[i + 1]
      const isCommaBetweenDayAndYear =
        prev?.type === "day" && next?.type === "year" && /^,\s*$/.test(p.value)
      if (isCommaBetweenDayAndYear) {
        out += " "
        continue
      }
    }
    out += p.value
  }
  return out.trim()
}

/**
 * Convert UTC ISO → formatted display in the scheduling timezone.
 */
export const utcToLocalDisplay = (utcString, clientOrTimeZone) => {
  if (!utcString) return ""

  const parsed = parseUtcMoment(utcString)
  if (!parsed) return ""

  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  const date = parsed.toDate()

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
    timeZoneName: "shortGeneric",
  })
}

/**
 * Format UTC for confirmation / review (date + time in client zone).
 */
export const formatUtcForUser = (utcString, clientOrTimeZone) => {
  if (!utcString) return { date: "", time: "" }

  const parsed = parseUtcMoment(utcString)
  if (!parsed) return { date: "", time: "" }

  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  const date = parsed.toDate()

  return {
    date: formatDateForZoneDisplay(date, tz),
    time: date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
    }),
  }
}

/**
 * Scheduled/publish datetime for cards and detail panels.
 */
export function formatPostPublishLabel(utcString, fallbackDate, clientOrTimeZone) {
  if (utcString) {
    const { date, time } = formatUtcForUser(utcString, clientOrTimeZone)
    if (date && time) return `${date}, ${time}`
    return date || time || ""
  }
  if (fallbackDate) {
    const d = fallbackDate instanceof Date ? fallbackDate : new Date(fallbackDate)
    if (!Number.isNaN(d.getTime())) {
      const tz = resolveSchedulingTimeZone(clientOrTimeZone)
      const date = formatDateForZoneDisplay(d, tz)
      const time = d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      })
      return `${date}, ${time}`
    }
  }
  return ""
}

/**
 * Convert UTC ISO → display WITH timezone abbreviation (CDT / IST).
 */
export const utcToLocalDisplayWithTZ = (utcString, clientOrTimeZone) => {
  if (!utcString) return ""

  const parsed = parseUtcMoment(utcString)
  if (!parsed) return ""

  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  return parsed.clone().tz(tz).format("DD MMM YYYY, hh:mm A z")
}

/**
 * Calendar day key (YYYY-MM-DD) for a UTC instant in the scheduling timezone.
 * Date-only strings are returned as-is (not treated as UTC midnight).
 */
export const utcToLocalDateKey = (utcString, clientOrTimeZone) => {
  if (!utcString) return null

  const raw = String(utcString).trim()
  if (DATE_ONLY_KEY.test(raw)) return raw

  const parsed = parseUtcMoment(raw)
  if (!parsed) return null

  const tz = resolveSchedulingTimeZone(clientOrTimeZone)
  return parsed.clone().tz(tz).format("YYYY-MM-DD")
}

/**
 * Short label (e.g. "Aug 1") for a UTC instant or date-only key in the given IANA zone.
 * Date-only strings are shown as calendar dates without timezone shift.
 */
export function formatShortDateInTimeZone(value, timeZone) {
  if (value == null || value === "") return ""

  const raw = String(value).trim()
  if (!raw) return ""

  if (DATE_ONLY_KEY.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number)
    if (!year || !month || !day) return ""
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  }

  const parsed = parseUtcMoment(raw)
  if (!parsed) return ""

  const tz = getClientIanaTimeZone(timeZone) || resolveSchedulingTimeZone(timeZone)
  return parsed.clone().tz(tz).format("MMM D")
}

/**
 * Whether a post/API instant falls on a calendar day within an inclusive local date range.
 * Range bounds use the dashboard date picker's local calendar days; instant day uses `timeZone`.
 */
export function isCalendarDayInRange(instant, rangeStart, rangeEnd, timeZone) {
  if (!timeZone || rangeStart == null || rangeEnd == null) return true

  const dayKey = utcToLocalDateKey(instant, timeZone)
  if (!dayKey) return true

  const toRangeKey = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const y = value.getFullYear()
      const m = String(value.getMonth() + 1).padStart(2, "0")
      const d = String(value.getDate()).padStart(2, "0")
      return `${y}-${m}-${d}`
    }
    const raw = String(value ?? "").trim()
    if (DATE_ONLY_KEY.test(raw)) return raw
    const parsed = parseUtcMoment(raw)
    if (!parsed) return null
    const tz = getClientIanaTimeZone(timeZone) || resolveSchedulingTimeZone(timeZone)
    return parsed.clone().tz(tz).format("YYYY-MM-DD")
  }

  const startKey = toRangeKey(rangeStart)
  const endKey = toRangeKey(rangeEnd)
  if (!startKey || !endKey) return true

  return dayKey >= startKey && dayKey <= endKey
}

export function formatPostPublishLabelWithTZ(utcString, fallbackDate, clientOrTimeZone) {
  if (utcString) {
    return utcToLocalDisplayWithTZ(utcString, clientOrTimeZone)
  }

  if (fallbackDate) {
    const tz = resolveSchedulingTimeZone(clientOrTimeZone)
    return moment(fallbackDate).tz(tz).format("DD MMM YYYY, hh:mm A z")
  }

  return ""
}

export const getRelativeTimeAgo = (utcDate) => {
  if (!utcDate) return "Just now"

  const now = new Date()
  const past = new Date(utcDate.includes("Z") ? utcDate : utcDate + "Z")

  const diffInSeconds = Math.floor((now - past) / 1000)

  if (diffInSeconds < 60) return "Just now"

  const minutes = Math.floor(diffInSeconds / 60)
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`

  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`

  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks} week${weeks > 1 ? "s" : ""} ago`

  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`

  const years = Math.floor(days / 365)
  return `${years} year${years > 1 ? "s" : ""} ago`
}
